/**
 * Centralized URL normalization utilities
 * Handles API base URL construction and prevents duplication
 */

export interface NormalizedUrls {
  /** Base URL for generated API clients (ends with /api) */
  apiBase: string;
  /** Root URL for CSRF and other non-API requests (no /api suffix) */
  rootBase: string;
}

/**
 * Configuration for API context path
 */
export interface ApiContextConfig {
  /** The context path for API endpoints (default: '/api') */
  contextPath: string;
}

/**
 * Normalizes an API base URL to ensure proper construction
 * Prevents duplication of /api paths and handles edge cases
 * 
 * @param baseUrl - The base URL from environment or configuration
 * @param config - Optional configuration for context path
 * @returns Object with normalized apiBase and rootBase URLs
 */
export function normalizeApiBaseUrl(baseUrl: string, config?: ApiContextConfig): NormalizedUrls {
  // Remove trailing slashes
  const rawBase = baseUrl.replace(/\/$/, '');
  const contextPath = config?.contextPath || '/api';
  
  // Check if the URL already contains the context path to avoid duplication
  const contextRegex = new RegExp(`\\${contextPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`);
  const contextInPathRegex = new RegExp(`\\${contextPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/`);
  
  const hasContextSuffix = contextRegex.test(rawBase);
  const hasContextInPath = contextInPathRegex.test(rawBase);
  
  let apiBase: string;
  let rootBase: string;
  
  if (hasContextSuffix) {
    // URL already ends with context path
    apiBase = rawBase;
    rootBase = rawBase.replace(contextRegex, '');
  } else if (hasContextInPath) {
    // URL contains context path somewhere in the path, ensure it ends with context path
    const contextPathEscaped = contextPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    apiBase = rawBase.replace(new RegExp(`\\${contextPathEscaped}.*$`), contextPath);
    rootBase = rawBase.replace(new RegExp(`\\${contextPathEscaped}.*$`), '');
  } else {
    // No context path in URL, add it
    apiBase = `${rawBase}${contextPath}`;
    rootBase = rawBase;
  }
  
  // Ensure rootBase is not empty
  if (!rootBase) {
    rootBase = rawBase;
  }
  
  return { apiBase, rootBase };
}

/**
 * Gets the default API base URL from environment variables
 * @returns The default API base URL
 */
export function getDefaultApiBaseUrl(): string {
  return process.env.NEXT_PUBLIC_API_BASE_URL as string;
}

/**
 * Normalizes the default API base URL
 * @param config - Optional configuration for context path
 * @returns Normalized URLs using the default base
 */
export function normalizeDefaultApiBaseUrl(config?: ApiContextConfig): NormalizedUrls {
  return normalizeApiBaseUrl(getDefaultApiBaseUrl(), config);
}

/**
 * Gets the API context path from environment variables or defaults to '/api'
 * @returns The API context path
 */
export function getApiContextPath(): string {
  return process.env.NEXT_PUBLIC_API_CONTEXT_PATH || '/api';
}

/**
 * Normalizes a WebSocket URL for session connections
 * WebSocket handlers are registered under the API context path (e.g., /api/ws/sessions/{sessionId})
 * @param baseUrl - The base URL from environment or configuration
 * @param sessionId - The session ID to connect to
 * @param config - Optional configuration for context path
 * @returns The normalized WebSocket URL
 */
export function normalizeWebSocketUrl(baseUrl: string, sessionId: string, config?: ApiContextConfig): string {
  // WebSocket handlers are registered at /api/ws/sessions/{sessionId} (with /api prefix)
  // So we use apiBase to include the context path
  const { apiBase } = normalizeApiBaseUrl(baseUrl, config);
  
  // Convert HTTP/HTTPS to WS/WSS and construct WebSocket URL
  const wsBase = apiBase.replace(/^https:\/\//, 'wss://').replace(/^http:\/\//, 'ws://');
  return `${wsBase}/ws/sessions/${sessionId}`;
}
